import { useState, useEffect } from "react";
import { useLoaderData, useFetcher, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import type { BillingData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import styles from "./BillingPage.module.css";

interface UrlResult {
  url: string;
}

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  enterprise: "Enterprise",
  staff: "Staff",
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: styles.statusActive },
  past_due: { label: "Past Due", className: styles.statusWarning },
  canceled: { label: "Canceled", className: styles.statusError },
  incomplete: { label: "Incomplete", className: styles.statusWarning },
};

function formatDate(dateString: string | null): string {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

export default function BillingPage() {
  const { organization, billing, invoices } = useLoaderData() as BillingData;
  const [searchParams] = useSearchParams();
  const checkoutFetcher = useFetcher();
  const portalFetcher = useFetcher();
  const cancelFetcher = useFetcher();

  const [selectedInterval, setSelectedInterval] = useState<"yearly" | "monthly">("yearly");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isCheckoutLoading = checkoutFetcher.state === "submitting";
  const isPortalLoading = portalFetcher.state === "submitting";
  const isCancelLoading = cancelFetcher.state === "submitting";

  const statusInfo = STATUS_LABELS[billing.status] ?? {
    label: "Active",
    className: styles.statusActive,
  };

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      toast.success("Subscription activated successfully!");
    } else if (checkout === "canceled") {
      toast.error("Checkout was canceled");
    }
  }, [searchParams]);

  useEffect(() => {
    if (checkoutFetcher.data) {
      const result = checkoutFetcher.data as ActionResult<UrlResult>;
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [checkoutFetcher.data]);

  useEffect(() => {
    if (portalFetcher.data) {
      const result = portalFetcher.data as ActionResult<UrlResult>;
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [portalFetcher.data]);

  useEffect(() => {
    if (cancelFetcher.data) {
      const result = cancelFetcher.data as ActionResult;
      if (result.success) {
        toast.success("Subscription will be canceled at the end of the billing period");
        setShowCancelConfirm(false);
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [cancelFetcher.data]);

  if (!billing.stripeConfigured) {
    return (
      <>
        <Header />
        <div className="container">
          <div className={styles.container}>
            <div className={styles.header}>
              <h1>Billing</h1>
              <LinkButton to={`/organizations/${organization.id}`} variant="secondary">
                Back to Organization
              </LinkButton>
            </div>
            <div className={styles.section}>
              <div className={styles.notice}>
                <p>Billing is not configured for this application.</p>
                <p className={styles.helpText}>All features are unlocked.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>Billing</h1>
            <LinkButton to={`/organizations/${organization.id}`} variant="secondary">
              Back to Organization
            </LinkButton>
          </div>

          <div className={styles.section}>
            <h2>Current Plan</h2>
            <div className={styles.planStatus}>
              <div className={styles.planInfo}>
                <div className={styles.planTier}>
                  <span className={styles.tierBadge}>{TIER_LABELS[billing.tier]}</span>
                  <span className={`${styles.statusBadge} ${statusInfo.className}`}>
                    {statusInfo.label}
                  </span>
                </div>
                {billing.tier !== "free" && billing.currentPeriodEnd && (
                  <p className={styles.periodInfo}>
                    {billing.cancelAtPeriodEnd ? (
                      <>Cancels on {formatDate(billing.currentPeriodEnd)}</>
                    ) : (
                      <>Renews on {formatDate(billing.currentPeriodEnd)}</>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2>Usage</h2>
            <div className={styles.usageGrid}>
              <div className={styles.usageCard}>
                <div className={styles.usageHeader}>
                  <span className={styles.usageLabel}>Members</span>
                  <span className={styles.usageCount}>
                    {billing.usage.seats} /{" "}
                    {billing.limits.maxSeats === null || billing.limits.maxSeats === Infinity
                      ? "Unlimited"
                      : billing.limits.maxSeats}
                  </span>
                </div>
                <div className={styles.usageBar}>
                  <div
                    className={`${styles.usageFill} ${billing.usage.seatsPercent >= 90 ? styles.usageFillWarning : ""}`}
                    style={{ width: `${Math.min(billing.usage.seatsPercent, 100)}%` }}
                  />
                </div>
              </div>
              <div className={styles.usageCard}>
                <div className={styles.usageHeader}>
                  <span className={styles.usageLabel}>Podcasts</span>
                  <span className={styles.usageCount}>
                    {billing.usage.teams} /{" "}
                    {billing.limits.maxTeams === null || billing.limits.maxTeams === Infinity
                      ? "Unlimited"
                      : billing.limits.maxTeams}
                  </span>
                </div>
                <div className={styles.usageBar}>
                  <div
                    className={`${styles.usageFill} ${billing.usage.teamsPercent >= 90 ? styles.usageFillWarning : ""}`}
                    style={{ width: `${Math.min(billing.usage.teamsPercent, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {billing.tier === "free" && (
            <div className={`${styles.section} ${styles.upgradeSection}`}>
              <h2>Upgrade to Pro</h2>
              <p className={styles.sectionDescription}>
                Unlock more podcasts, members, and premium features with a Pro subscription.
              </p>

              <div className={styles.intervalToggle}>
                <button
                  type="button"
                  className={`${styles.intervalBtn} ${selectedInterval === "yearly" ? styles.intervalBtnActive : ""}`}
                  onClick={() => setSelectedInterval("yearly")}
                >
                  Yearly
                  <span className={styles.discountBadge}>Save 17%</span>
                </button>
                <button
                  type="button"
                  className={`${styles.intervalBtn} ${selectedInterval === "monthly" ? styles.intervalBtnActive : ""}`}
                  onClick={() => setSelectedInterval("monthly")}
                >
                  Monthly
                </button>
              </div>

              <div className={styles.planCard}>
                <div className={styles.planCardHeader}>
                  <h3>Pro</h3>
                  <div className={styles.price}>
                    {selectedInterval === "yearly" ? (
                      <>
                        <span className={styles.priceAmount}>$24</span>
                        <span className={styles.pricePeriod}>/month</span>
                        <span className={styles.priceNote}>billed annually</span>
                      </>
                    ) : (
                      <>
                        <span className={styles.priceAmount}>$29</span>
                        <span className={styles.pricePeriod}>/month</span>
                      </>
                    )}
                  </div>
                </div>
                <ul className={styles.planFeatures}>
                  <li>Up to 10 members</li>
                  <li>Up to 5 podcasts</li>
                  <li>Advanced analytics</li>
                  <li>Priority support</li>
                </ul>
                <checkoutFetcher.Form
                  method="post"
                  action={`/organizations/${organization.id}/billing/checkout`}
                >
                  <input type="hidden" name="interval" value={selectedInterval} />
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    loading={isCheckoutLoading}
                    loadingText="Processing..."
                    className={styles.upgradeBtn}
                  >
                    Upgrade Now
                  </Button>
                </checkoutFetcher.Form>
              </div>
            </div>
          )}

          {billing.tier === "pro" && (
            <>
              <div className={styles.section}>
                <h2>Payment Method</h2>
                <p className={styles.sectionDescription}>
                  Manage your payment method and billing details in the Stripe Customer Portal.
                </p>
                <portalFetcher.Form
                  method="post"
                  action={`/organizations/${organization.id}/billing/portal`}
                >
                  <Button
                    type="submit"
                    variant="secondary"
                    loading={isPortalLoading}
                    loadingText="Loading..."
                  >
                    Manage Payment Method
                  </Button>
                </portalFetcher.Form>
              </div>

              {invoices.length > 0 && (
                <div className={styles.section}>
                  <h2>Invoice History</h2>
                  <div className={styles.invoiceList}>
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className={styles.invoiceItem}>
                        <div className={styles.invoiceInfo}>
                          <span className={styles.invoiceNumber}>{invoice.number || "Draft"}</span>
                          <span className={styles.invoiceDate}>
                            {new Date(invoice.created * 1000).toLocaleDateString()}
                          </span>
                        </div>
                        <div className={styles.invoiceAmount}>
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </div>
                        <div className={styles.invoiceActions}>
                          {invoice.hostedInvoiceUrl && (
                            <a
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.invoiceLink}
                            >
                              View
                            </a>
                          )}
                          {invoice.pdfUrl && (
                            <a
                              href={invoice.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.invoiceLink}
                            >
                              PDF
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!billing.cancelAtPeriodEnd && (
                <div className={`${styles.section} ${styles.cancelSection}`}>
                  <h2>Cancel Subscription</h2>
                  <div className={styles.cancelContent}>
                    <div>
                      <p>
                        Your subscription will remain active until the end of your current billing
                        period. After that, your organization will be downgraded to the Free tier.
                      </p>
                      <p className={styles.helpText}>
                        Your data will be preserved, but you won't be able to add new members or
                        podcasts beyond Free tier limits.
                      </p>
                    </div>
                    {!showCancelConfirm ? (
                      <Button
                        type="button"
                        variant="dangerOutline"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        Cancel Subscription
                      </Button>
                    ) : (
                      <div className={styles.cancelConfirm}>
                        <p className={styles.confirmText}>Are you sure you want to cancel?</p>
                        <div className={styles.confirmActions}>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setShowCancelConfirm(false)}
                            disabled={isCancelLoading}
                          >
                            Keep Subscription
                          </Button>
                          <cancelFetcher.Form
                            method="post"
                            action={`/organizations/${organization.id}/billing/cancel`}
                          >
                            <Button
                              type="submit"
                              variant="danger"
                              loading={isCancelLoading}
                              loadingText="Canceling..."
                            >
                              Yes, Cancel
                            </Button>
                          </cancelFetcher.Form>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {billing.tier === "enterprise" && (
            <div className={styles.section}>
              <div className={`${styles.notice} ${styles.enterpriseNotice}`}>
                <h3>Enterprise Plan</h3>
                <p>
                  Your organization is on an Enterprise plan with custom limits. Contact your
                  account manager for billing inquiries.
                </p>
              </div>
            </div>
          )}

          {billing.tier === "staff" && (
            <div className={styles.section}>
              <div className={`${styles.notice} ${styles.enterpriseNotice}`}>
                <h3>Staff Plan</h3>
                <p>Your organization is on an internal Staff plan with all features unlocked.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
